import { Dictionary } from '@skylab-shared/types';
import { isFunction, isString, isUndefined } from '@skylab-shared/utils';

export interface $Element {
    element: HTMLElement;
    on: (eventName: string, fnc: (e?: $EventKinds) => unknown, useCapture?: boolean) => void;
    off: (eventName: string) => void;

    appendTo: (selector: string) => void;
    append: (child: $ElementKinds | Node) => void;
    prepend: (child: $ElementKinds | Node) => void;
    clone: () => $Element;
    empty: () => void;
    remove: () => void;

    prev: () => $Element;
    next: () => $Element;
    parent: () => $Element;
    findParent: (selector: string) => $Element;
    childrens: () => $Element[];
    find: (selector: string) => $Element;
    findAll: (selector: string) => $Element[];
    is: (selector: string) => boolean;
    isVisible: () => boolean;
    hasChild: (element: $ElementKinds) => boolean;
    hasFocus: () => boolean;

    hasAttr: (attrName: string) => boolean;
    getAttr: (attrName: string) => string;
    setAttr: (attrName: string, value: string | number) => void;
    removeAttr: (attrName: string) => void;
    hasClass: (className: string) => boolean;
    addClass: (className: string) => void;
    removeClass: (className: string) => void;
    setCss: (rules: Partial<CSSStyleDeclaration>) => void;
    getCss: (rule: string) => string;
    innerHTML: (value: string) => string;
    innerText: () => string;

    width: () => number;
    innerWidth: () => number;
    outerWidth: () => number;
    setWidth: (value: number) => void;
    height: () => number;
    innerHeight: () => number;
    setHeight: (value: number) => void;
    offset: () => ClientRect;
    getScrollLeft: () => number;
    setScrollLeft: (value: number) => number;
    getScrollTop: () => number;
    setScrollTop: (value: number) => number;
    scrollIntoView: (options?: boolean | ScrollIntoViewOptions) => void;
    getVal: () => string;
    setVal: (value: string) => string;
    hide: () => void;
    show: () => void;

    click: () => void;
    focus: () => void;
    blur: () => void;
    select: () => void;
}

export function $all(selector: string, ctx: $ElementKinds = document): $Element[] {
    return Array.from((<HTMLElement>ctx).querySelectorAll<HTMLElement>(selector)).map(e => $(e));
}
type $EventKinds = Event;

type $ElementKinds = HTMLElement | Element | Document | Node;

export function $(arg: string | $ElementKinds | null | undefined, ctx: $ElementKinds = document): $Element {
    if (!arg) return null as any;

    const _element = (isString(arg)
        ? (<HTMLElement>ctx).querySelector<HTMLElement>(arg)
        : <HTMLElement>arg) as HTMLElement;

    const _listeners: Dictionary<{fnc: EventListener, useCapture: boolean}> = {}; // allows to implement namespaced events like `keydown.${uuid()}`
    return {
        get element(): HTMLElement {
            return _element;
        },
        on: (eventName: string, fnc: (e?: $EventKinds) => unknown, useCapture?: boolean): void => {
            if (_listeners[eventName]) {
                _element.removeEventListener(eventName.split('.')[0], _listeners[eventName].fnc, _listeners[eventName].useCapture);
                delete _listeners[eventName];
                // remove event if it is overwritten with same namespace
            }
            _listeners[eventName] = { fnc: fnc, useCapture: !!useCapture };
            _element.addEventListener(eventName.split('.')[0], _listeners[eventName].fnc, _listeners[eventName].useCapture);
        },
        off: (eventName: string): void => {
            if (!_listeners[eventName]) return;
            _element.removeEventListener(eventName.split('.')[0], _listeners[eventName].fnc, _listeners[eventName].useCapture);
            delete _listeners[eventName];
        },

        appendTo: (selector: string): void => $(selector).append(_element),
        append: (child: $ElementKinds): void => _element.append(child),
        prepend: (child: $ElementKinds): void => _element.prepend(child),
        clone: (): $Element => $(_element.cloneNode(true)),
        innerHTML: (value: string) => _element.innerHTML = value,
        innerText: () => _element.innerText,
        empty: (): void => {
            while (_element.firstChild) {
                _element.removeChild(_element.firstChild);
            }
        },
        remove: (): void => _element.remove(),

        prev: (): $Element => $(_element.previousElementSibling),
        next: (): $Element => $(_element.nextElementSibling),
        parent: (): $Element => $(_element.parentElement),
        findParent: (selector: string): $Element => $(_element.closest(selector)),
        childrens: (): $Element[] => {
            if (!_element.children) return [];
            return Array.from(_element.children).map(c => $(c));
        },
        find: (selector: string): $Element => $(_element.querySelector<HTMLElement>(selector)),
        findAll: (selector: string): $Element[] => Array.from(_element.querySelectorAll<HTMLElement>(selector)).map(e => $(e)),
        is: (selector: string): boolean => _element.matches(selector),
        isVisible: (): boolean => _element.offsetWidth > 0 && _element.offsetHeight > 0,
        hasChild: (element: $ElementKinds): boolean => {
            let elementFound: boolean = false;
            const searchInChilds = (parent: HTMLElement) => {
                if (!parent.children.length || elementFound) return;
                for (let i = 0; i < parent.children.length; i++) {
                    if (elementFound) return;
                    if (parent.childNodes.item(i) === element) {
                        elementFound = true;
                    } else {
                        searchInChilds(<HTMLElement>parent.children.item(i));
                    }
                }
            };
            searchInChilds(<HTMLElement>_element);
            return elementFound;
        },
        hasFocus: (): boolean => {
            const currentlyActive = document.activeElement;
            if (!currentlyActive) return false;
            const currentlyActiveTagName = currentlyActive.tagName;
            if (currentlyActive === _element) return true;
            const elementsWithSameTags = Array.from(_element.getElementsByTagName(currentlyActiveTagName));
            return elementsWithSameTags.find(e => e === currentlyActive) !== void 0;
        },

        hasAttr: (attrName: string): boolean => _element.hasAttribute(attrName),
        getAttr: (attrName: string): string => _element.getAttribute(attrName) ?? '',
        setAttr: (attrName: string, value: string | number): void => isUndefined(value) ? void 0 : _element.setAttribute(attrName, value.toString()),
        removeAttr: (attrName: string): void => _element.removeAttribute(attrName),
        hasClass: (className: string): boolean => _element.classList.contains(className),
        addClass: (className: string): void => {
            const classNames = className.split(' ');
            classNames.forEach(c => {
                _element.classList.add(c);
            });
        },
        removeClass: (className: string): void => _element.classList.remove(className),
        setCss: (rules: Partial<CSSStyleDeclaration>): void => {
            if (!rules) return;
            for (const rule in rules) {
                (_element.style as any)[rule] = rules[rule];
            }
        },
        getCss: (rule: string): string => {
            const styles = window.getComputedStyle(_element);
            return (styles as any)[rule];
        },
        width: (): number => _element.clientWidth,
        innerWidth: (): number => {
            const styles = window.getComputedStyle(_element);
            const paddingLeft = styles.paddingLeft ? parseFloat(styles.paddingLeft) : 0;
            const paddingRight = styles.paddingLeft ? parseFloat(styles.paddingLeft) : 0;
            const padding = paddingLeft + paddingRight;
            return _element.clientWidth - padding;
        },
        outerWidth: (): number => {
            let width = _element.offsetWidth;
            const style = getComputedStyle(_element);
            width += parseInt(style.marginLeft || '0') + parseInt(style.marginRight || '0');
            return width;
        },
        setWidth: (value: number): void => {
            const styles = window.getComputedStyle(_element);
            const paddingLeft = styles.paddingLeft ? parseFloat(styles.paddingLeft) : 0;
            const paddingRight = styles.paddingLeft ? parseFloat(styles.paddingLeft) : 0;
            const padding = paddingLeft + paddingRight;
            _element.style.width = `${value - padding}px`;
        },
        height: (): number => _element.clientHeight,
        innerHeight: (): number => {
            const styles = window.getComputedStyle(_element);
            const paddingTop = styles.paddingTop ? parseFloat(styles.paddingTop) : 0;
            const paddingBottom = styles.paddingBottom ? parseFloat(styles.paddingBottom) : 0;
            const padding = paddingTop + paddingBottom;
            return _element.clientHeight - padding;
        },
        setHeight: (value: number): void => {
            const styles = window.getComputedStyle(_element);
            const paddingTop = styles.paddingTop ? parseFloat(styles.paddingTop) : 0;
            const paddingBottom = styles.paddingBottom ? parseFloat(styles.paddingBottom) : 0;
            const padding = paddingTop + paddingBottom;
            _element.style.height = `${value - padding}px`;
        },
        offset: (): ClientRect => _element.getBoundingClientRect(),
        getVal: (): string => (<HTMLInputElement>_element).value,
        setVal: (value: string): string => (<HTMLInputElement>_element).value = value,
        hide: () => _element.style.display = 'none',
        show: () => _element.style.display = 'block',

        click: (): void => _element.click(),
        focus: (): void => _element.focus(),
        blur: (): void => _element.blur(),
        select: (): void => isFunction((<HTMLInputElement>_element).select) ? (<HTMLInputElement>_element).select() : void 0,
        getScrollLeft: (): number => _element.scrollLeft,
        setScrollLeft: (value: number): number => _element.scrollLeft = value,
        getScrollTop: (): number => _element.scrollTop,
        setScrollTop: (value: number): number => _element.scrollTop = value,
        scrollIntoView: (options?: boolean | ScrollIntoViewOptions): void => _element.scrollIntoView(options)
    };
}
